import { InlineStack, Select } from '@customerio/pluma-components/react';
import { collectionFilterBuilder } from './builder';
import { OperatorValue } from './operator-value';
import { COLLECTION_ATTRIBUTE_CONDITION_TYPE, type AttributeFilterState } from './dryad-utils';

type CollectionAttributeRowProps = {
  id: string;
};

function uniqueOptions(names: string[]) {
  return [...new Set(names)].map((name) => ({ value: name, label: name }));
}

// Collection filter values have no suggestions yet: the value endpoints behind
// them are people-attribute scoped, and there is no collection-scoped source to
// query. OperatorValue keeps the options/loading props so wiring one up later is
// a change of argument, not of shape.
const EMPTY_VALUE_OPTIONS: { value: string; label: string }[] = [];

export function CollectionAttributeRow({ id }: CollectionAttributeRowProps) {
  const { node, state } = collectionFilterBuilder.useLeafState(id, COLLECTION_ATTRIBUTE_CONDITION_TYPE);
  const { updateState } = collectionFilterBuilder.useBuilderActions();
  const meta = collectionFilterBuilder.useMeta();

  if (node == null) {
    return null;
  }

  const schemaOptions = uniqueOptions([...meta.schema, ...(state.field === '' ? [] : [state.field])]);
  const update = (patch: Partial<AttributeFilterState>) => updateState(node, patch);

  return (
    <InlineStack alignBlock="center" gap="050" wrap>
      <Select
        ariaLabel="Collection attribute"
        value={state.field}
        options={schemaOptions}
        placeholder="Select an attribute"
        searchPlaceholder="Search attributes"
        isSearchable
        withCreate
        onCreate={(value) => update({ field: value })}
        onChange={(value) => update({ field: value ?? '' })}
        size="sm"
        isDisabled={meta.isDisabled}
      />
      <OperatorValue
        field={state.field}
        isLoadingValueOptions={false}
        operator={state.operator}
        value={state.value}
        valueOptions={EMPTY_VALUE_OPTIONS}
        value2={state.value2}
        disabled={meta.isDisabled}
        onChange={update}
      />
    </InlineStack>
  );
}
